<template>
  <div>
    <!-- TOOL BAR -->
    <v-row>
      <v-col cols="12" lg="4" md="6" sm="6" xs="12">
        <v-text-field
          solo
          autofocus
          v-model="searchQuery"
          hide-details
          prepend-inner-icon="mdi-magnify"
          placeholder="Search for companies..."
          persistent-hint
          clearable></v-text-field>
      </v-col>
      <v-col cols="12" lg="3" md="6" sm="6" xs="12">
        <div class="d-flex align-center">
          <v-select
            solo
            class="align-stretch font-weight-bold"
            v-model="selectedTenants"
            :items="tenants"
            chips
            hide-details
            item-text="name"
            item-value="id"
            :menu-props="{ maxHeight: '400' }"
            label="Select tenants"
            multiple
            :persistent-hint="false">
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < visibleTenantFilterChipLength">
                <span>{{ item.name }}</span>
              </v-chip>
              <span v-if="index === visibleTenantFilterChipLength" class="grey--text text-caption">
                (+{{ selectedTenants.length - visibleTenantFilterChipLength }})
              </span>
            </template>
          </v-select>
        </div>
      </v-col>
      <v-col cols="12" lg="3" md="6" sm="6" xs="12">
        <v-select
          solo
          class="align-stretch font-weight-bold"
          v-model="selectedCategory"
          :items="companyCategories"
          clearable
          hide-details
          item-text="text"
          item-value="value"
          :menu-props="{ maxHeight: '400' }"
          label="Select category"
          :persistent-hint="false"></v-select>
      </v-col>
      <v-col lg="2" v-if="canManageCompanies">
        <v-btn @click="openCreateCompanyForm" color="accent" style="height: 46px; width: 100%; font-size: 13px">
          <v-icon class="mr-3">mdi-domain</v-icon>
          CREATE COMPANY
        </v-btn>
      </v-col>
    </v-row>
    <!-- DATA TABLE -->
    <v-data-table
      :headers="headers"
      :items="companies"
      :loading="loading"
      :server-items-length="companiesTotalCount"
      :items-per-page="options.itemsPerPage"
      :options.sync="options"
      class="elevation-1 mt-5"
      :dense="$vuetify.breakpoint.width <= 1920"
      :footer-props="{
        'items-per-page-options': [50, 100],
      }">
      <template v-slot:[`item.name`]="{ item }">
        <v-icon class="mr-2">mdi-domain</v-icon>
        <b>{{ item.name }}</b>
      </template>

      <template v-slot:[`item.organizationNumber`]="{ item }">
        <copyToClipboardField :item="item.organizationNumber"></copyToClipboardField>
      </template>

      <template v-slot:[`item.fortnoxCustomerId`]="{ item }">
        <copyToClipboardField :item="item.fortnoxCustomerId"></copyToClipboardField>
      </template>

      <template v-slot:[`item.email`]="{ item }">
        <copyToClipboardField :item="item.email"></copyToClipboardField>
      </template>

      <template v-slot:[`item.remainingLimit.plainText`]="{ item }">
        <b :class="item.limit.amount > 0 ? 'greeny--text' : 'primary--text'">{{ item.remainingLimit.plainText }} </b>
      </template>

      <template v-slot:[`item.tenants`]="{ item }">
        <v-chip
          v-for="tenant in item.tenants"
          :key="tenant.id"
          color="secondary"
          class="font-weight-bold cliporder mr-1 mb-1"
          link
          small>
          {{ tenant.name }}
        </v-chip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div>
          <v-btn
            icon
            @click="
              companyData = normalizeCompanyData(item);
              initCustomersByCompanyId(item.id);
              detailDialog = true;
            ">
            <v-icon color="accent">mdi-eye</v-icon>
          </v-btn>
          <v-btn
            icon
            v-if="canManageCompanies"
            @click="
              companyData = normalizeCompanyData(item);
              initCustomersByCompanyId(item.id);
              dialog = true;
              editing = true;
            ">
            <v-icon color="third">mdi-pencil</v-icon>
          </v-btn>
          <v-btn v-if="isSuperAdmin" @click="deleteCompany(item.id)" icon>
            <v-icon color="primary">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- CREATE & UPDATE DIALOG -->
    <v-dialog v-model="dialog" :persistent="isLoading" :loading="isLoading" max-width="100%" scrollable>
      <v-form ref="companyForm">
        <v-card>
          <v-toolbar fixed color="titlebg" class="title" dense dark>
            <v-toolbar-title>{{ editing ? "Update" : "Create" }} Company</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text>
            <div class="form-section">
              <h3 class="my-3 mt-0">Company Information</h3>

              <v-row>
                <v-col cols="12" lg="3" md="4" sm="6" xs="12">
                  <v-text-field
                    v-model="companyData.name"
                    :rules="validate.input"
                    outlined
                    label="Company Name"
                    dense
                    hide-details
                    clearable />
                </v-col>

                <v-col cols="12" lg="3" md="4" sm="6" xs="12">
                  <v-text-field
                    outlined
                    label="Organization Number"
                    hide-details
                    :rules="validate.input"
                    clearable
                    dense
                    v-model="companyData.organizationNumber"
                    required />
                </v-col>

                <v-col cols="12" lg="3" md="4" sm="12" xs="12">
                  <v-select
                    v-model="companyData.tenants"
                    :items="tenants"
                    outlined
                    dense
                    multiple
                    clearable
                    hide-details
                    item-text="name"
                    item-value="id"
                    :menu-props="{ maxHeight: '400' }"
                    label="Tenants"
                    :persistent-hint="false">
                    <template v-slot:selection="{ item, index }">
                      <v-chip v-if="index < visibleChipLength" small>
                        <span>{{ item.name }}</span>
                      </v-chip>
                      <span v-if="index === visibleChipLength" class="grey--text text-caption">
                        (+{{ companyData?.tenants?.length - visibleChipLength }} more)
                      </span>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" lg="3" md="4" sm="12" xs="12">
                  <v-select
                    v-model="companyData.category"
                    :items="companyCategories"
                    outlined
                    dense
                    hide-details
                    item-text="text"
                    item-value="value"
                    :persistent-hint="false"
                    label="Category">
                  </v-select>
                </v-col>
              </v-row>

              <v-textarea
                class="mt-3"
                outlined
                rows="2"
                style="background-color: #fff6d9"
                label="Internal Message"
                hide-details
                clearable
                v-model="companyData.comment" />
            </div>

            <div class="form-section">
              <h3 class="my-3">Contact Person</h3>

              <v-row>
                <v-col cols="12" lg="6" md="6" sm="6" xs="12">
                  <v-text-field
                    outlined
                    label="Full Name"
                    :rules="validate.input"
                    dense
                    hide-details
                    clearable
                    v-model="companyData.contactPersonName" />
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="6" xs="12">
                  <CompanyPhoneInput
                    v-model="companyData.phone"
                    label="Contact Person's Phone Number"
                    required
                    clearable />
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="6" xs="12">
                  <v-text-field
                    outlined
                    label="Contact Person's E-mail"
                    :rules="validate.email"
                    dense
                    hide-details
                    clearable
                    v-model="companyData.email" />
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <h3 class="my-3">Reference</h3>
              <v-row>
                <v-col cols="12" lg="3" md="6" sm="6" xs="12">
                  <v-text-field
                    outlined
                    label="Email for Invoice or E-Invoice"
                    :rules="validate.email"
                    dense
                    hide-details
                    clearable
                    v-model="companyData.invoiceEmail" />
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="6" xs="12">
                  <v-text-field
                    outlined
                    label="Fortnox Customer ID"
                    dense
                    hide-details
                    clearable
                    v-model="companyData.fortnoxCustomerId" />
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="6" xs="12">
                  <v-text-field
                    outlined
                    label="Other / Branch"
                    hide-details
                    dense
                    clearable
                    v-model="companyData.reference" />
                </v-col>
              </v-row>
            </div>

            <div class="form-section">
              <!-- Finance -->
              <h3 class="my-3">Finance</h3>
              <v-row>
                <v-col cols="12" lg="3" md="6" sm="6">
                  <v-text-field
                    outlined
                    label="Limit"
                    hide-details
                    dense
                    clearable
                    type="number"
                    v-model="companyData.limit.amount"
                    v-on:change="companyData.limit.amount = parseFloat(companyData.limit.amount)" />
                </v-col>
                <v-col cols="12" lg="1" md="6" sm="6">
                  <v-select
                    outlined
                    v-model="companyData.limit.currency"
                    :items="['SEK']"
                    dense
                    hide-details
                    :menu-props="{ maxHeight: '400' }"
                    label="Select currency"
                    :persistent-hint="false"></v-select>
                </v-col>
                <v-col cols="12" lg="2" md="6" sm="6">
                  <v-select
                    outlined
                    v-model="companyData.allowedDaysToPay"
                    :items="[10, 15, 20, 30]"
                    dense
                    hide-details
                    :menu-props="{ maxHeight: '400' }"
                    label="Allowed days to pay the invoice"
                    :persistent-hint="false"
                    class="max-h"></v-select>
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="6" class="d-flex align-center">
                  <div>
                    <v-switch
                      v-model="companyData.automaticInvoiceFeeEnabled"
                      color="greeny"
                      hide-details
                      label="Automatically Add Invoice Fee in New Orders"
                      class="pa-0 ma-0" />
                  </div>
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="6" class="d-flex align-center">
                  <div>
                    <v-switch
                      v-model="companyData.trusted"
                      color="greeny"
                      hide-details
                      label="Allow Debiting by Invoice"
                      class="pa-0 ma-0 font-weight-bold" />
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Auto-Invoicing Through Fortnox -->
            <div class="form-section">
              <h3 class="my-3">Auto-Invoicing Through Fortnox</h3>
              <v-row>
                <v-col cols="12" lg="3" md="6" sm="6">
                  <v-select
                    outlined
                    v-model="companyData.invoicingFrequency"
                    :items="invoicingFrequencyItems"
                    item-text="text"
                    item-value="value"
                    dense
                    hide-details
                    :menu-props="{ maxHeight: '400' }"
                    label="Auto-Invoicing Frequency"
                    :persistent-hint="false"></v-select>
                </v-col>
                <v-col cols="12" lg="3" md="6" sm="6">
                  <v-select
                    outlined
                    v-model="companyData.invoicingMethod"
                    :items="invoicingMethodItems"
                    item-text="text"
                    item-value="value"
                    dense
                    hide-details
                    :menu-props="{ maxHeight: '400' }"
                    label="Auto-Invoicing Method"
                    :persistent-hint="false"
                    class="max-h"></v-select>
                </v-col>
              </v-row>
            </div>

            <!-- Default Codes -->
            <div class="form-section">
              <h3 class="my-3">Default Codes</h3>
              <v-row>
                <v-col cols="12" lg="6" md="6" sm="12">
                  <div class="add-box">
                    <span class="info-title font-weight-bold">Campaign Codes</span>

                    <div class="mt-2">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <v-text-field
                          v-model="newCampaignCode.code"
                          label="Campaign code"
                          outlined
                          dense
                          hide-details
                          clearable
                          style="max-width: 220px" />
                        <v-text-field
                          v-model="newCampaignCode.title"
                          :label="newCampaignCode.hidden ? 'Title (Required)' : 'Title (Optional)'"
                          outlined
                          dense
                          hide-details
                          clearable
                          class="ml-2"
                          style="max-width: 220px"
                          @keyup.enter="addCampaignCode" />
                        <div class="d-flex align-center ml-4 flex-shrink-0">
                          <v-checkbox
                            v-model="newCampaignCode.hidden"
                            label="Hide"
                            class="mt-0 pt-0 align-self-center"
                            hide-details />
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon small class="ml-1 align-self-center" color="grey" v-bind="attrs" v-on="on">
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span
                              >When a code is marked as hidden, only its title is visible to users. The code itself is
                              not displayed.</span
                            >
                          </v-tooltip>
                          <v-checkbox
                            v-model="newCampaignCode.applyByDefault"
                            label="Apply by default"
                            class="mt-0 pt-0 align-self-center ml-4"
                            hide-details />
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon small class="ml-1 align-self-center" color="grey" v-bind="attrs" v-on="on">
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <span
                              >When enabled, this code is applied automatically in searches without requiring user
                              selection.</span
                            >
                          </v-tooltip>
                        </div>
                        <v-btn color="accent" class="ml-auto flex-shrink-0" height="40" @click="addCampaignCode">
                          ADD CODE</v-btn
                        >
                      </div>
                    </div>
                    <v-spacer style="margin-bottom: 16px"></v-spacer>
                    <div
                      v-if="companyData.defaultCampaignCodes && companyData.defaultCampaignCodes.length"
                      class="mt-3 scroll-box">
                      <div v-for="(item, index) in companyData.defaultCampaignCodes" :key="index" class="mb-2">
                        <v-chip label class="mr-2" outlined>
                          <span class="chip-key">Code:</span><span class="chip-val">{{ item.code }}</span>
                          <span class="mx-2"></span>
                          <span class="chip-key">Title:</span><span class="chip-val">{{ item.title || "-" }}</span>
                          <span class="mx-2"></span>
                          <span class="chip-key">Hidden:</span>
                          <v-chip
                            x-small
                            :color="item.hidden ? 'green lighten-1' : 'grey lighten-2'"
                            :class="item.hidden ? 'chip-bool-yes' : 'chip-bool-no'"
                            class="field-status-chip">
                            {{ item.hidden ? "YES" : "NO" }}
                          </v-chip>
                          <span class="mx-2"></span>

                          <span class="chip-key">Apply by default:</span>
                          <v-chip
                            x-small
                            :color="item.applyByDefault ? 'green lighten-1' : 'grey lighten-2'"
                            :class="item.applyByDefault ? 'chip-bool-yes' : 'chip-bool-no'"
                            class="field-status-chip">
                            {{ item.applyByDefault ? "YES" : "NO" }}
                          </v-chip>
                          <v-icon small color="primary" class="chip-close-icon ml-2" @click="removeCampaignCode(index)">
                            mdi-close
                          </v-icon>
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </v-col>

                <template>
                  <v-col cols="12" lg="6" md="6" sm="12">
                    <div class="add-box">
                      <span class="info-title font-weight-bold">Agreement Codes</span>

                      <div class="mt-2">
                        <div class="d-flex align-center mb-3">
                          <div class="mr-2">
                            <v-text-field
                              v-model="newAgreementCode"
                              label="Agreement code"
                              outlined
                              dense
                              hide-details
                              clearable
                              @keyup.enter="addAgreementCode" />
                          </div>

                          <div class="mr-2">
                            <v-select
                              v-model="newAgreementCarrier"
                              :items="agreementCarriers"
                              item-text="text"
                              item-value="value"
                              return-object
                              label="Carrier"
                              outlined
                              dense
                              hide-details
                              clearable
                              class="mr-2" />
                          </div>

                          <v-btn color="accent" height="40" class="agreement-add-btn" @click="addAgreementCode">
                            ADD CODE
                          </v-btn>
                        </div>

                        <v-spacer style="margin-bottom: 16px"></v-spacer>
                        <div
                          v-if="companyData.defaultAgreementCodes && companyData.defaultAgreementCodes.length"
                          class="mt-1">
                          <v-chip
                            v-for="(item, index) in companyData.defaultAgreementCodes"
                            :key="index"
                            label
                            class="mr-2 mb-2"
                            outlined>
                            <span class="chip-key">Code:</span><span class="chip-val">{{ item.code }}</span>
                            <span class="mx-2"></span>
                            <span class="chip-key">Carrier:</span
                            ><span class="chip-val">{{ getCarrierText(item.issuer) }}</span>
                            <v-icon
                              small
                              color="primary"
                              class="chip-close-icon ml-2"
                              @click="removeAgreementCode(index)">
                              mdi-close
                            </v-icon>
                          </v-chip>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </template>
              </v-row>
            </div>

            <div class="form-section">
              <h3 class="mb-3 mt-6">Authorized Employees</h3>

              <div class="add-box">
                <p>You can add an employee just by the IATA number, or filling up the rest of the fields.</p>

                <v-form ref="employeeForm" class="mb-6">
                  <v-row>
                    <v-col cols="12" lg="2" md="4" sm="6" xs="12">
                      <v-text-field
                        outlined
                        label="First Name"
                        dense
                        hide-details="auto"
                        clearable
                        v-model="customer.firstName"
                        :rules="[
                          (v) =>
                            !!customer.firstName ||
                            (!!validate.input && !customer.lastName && !customer.email && !customer.phone) ||
                            'First name is required.',
                        ]" />
                    </v-col>

                    <v-col cols="12" lg="2" md="4" sm="6" xs="12">
                      <v-text-field
                        outlined
                        label="Last Name"
                        dense
                        hide-details="auto"
                        clearable
                        v-model="customer.lastName"
                        :rules="[
                          (v) =>
                            !!customer.lastName ||
                            (!!validate.input && !customer.firstName && !customer.email && !customer.phone) ||
                            'Last name is required.',
                        ]" />
                    </v-col>

                    <v-col cols="12" lg="2" md="4" sm="6" xs="12">
                      <v-text-field
                        outlined
                        label="E-Mail Address"
                        dense
                        hide-details="auto"
                        clearable
                        v-model="customer.email"
                        :rules="[
                          (v) =>
                            !!customer.email ||
                            (!!validate.input && !customer.firstName && !customer.lastName && !customer.phone) ||
                            'E-Mail address is required.',
                        ]" />
                    </v-col>

                    <v-col cols="12" lg="2" md="4" sm="6" xs="12">
                      <!-- Required unless the other fields are empty, like before; an IATA number makes it optional. -->
                      <CompanyPhoneInput
                        ref="employeePhone"
                        v-model="customer.phone"
                        label="Phone Number"
                        :required="
                          !customer.companyCustomerNumber &&
                          !!(customer.firstName || customer.lastName || customer.email)
                        "
                        clearable />
                    </v-col>

                    <v-col cols="12" lg="2" md="4" sm="6" xs="12">
                      <v-text-field
                        outlined
                        label="IATA Number"
                        hide-details="auto"
                        dense
                        clearable
                        v-model="customer.companyCustomerNumber" />
                    </v-col>

                    <v-col cols="12" lg="2" md="4" sm="6" xs="12">
                      <v-text-field
                        outlined
                        hide-details="auto"
                        dense
                        label="Reference"
                        clearable
                        v-model="customer.reference">
                        <template v-slot:append-outer>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on" color="grey" size="24"> mdi-information-outline </v-icon>
                            </template>
                            <span>This reference will be used in Fortnox automatic invoicing.</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" lg="2" md="4" sm="6" xs="12">
                      <AccountAutoComplete
                        @modelChange="accountChange($event)"
                        :initItem="customer"
                        :accountForCustomers="accountForCustomers"
                        :isEmployee="true"
                        dense />
                    </v-col>

                    <v-col v-if="canCreateAccount" cols="12" lg="2" md="4" sm="6" xs="12">
                      <v-btn
                        color="accent"
                        style="height: 40px"
                        dark
                        height=" 100%"
                        block
                        small
                        @click.prevent="
                          accountDialogKey++;
                          createAccountDialog = true;
                        ">
                        Create New Account
                      </v-btn>
                    </v-col>

                    <v-col cols="12" lg="4" md="4" sm="6" class="d-flex align-center">
                      <div class="d-flex align-center">
                        <v-switch
                          v-model="customer.companyRole"
                          true-value="employee_manager"
                          :false-value="null"
                          color="greeny"
                          hide-details
                          label="Can manage authorized employees"
                          class="pa-0 ma-0" />
                        <v-tooltip bottom max-width="280">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" color="grey" size="24" class="ml-2"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Allows adding, updating, and removing this company's authorized employees using the
                            selected account.</span
                          >
                        </v-tooltip>
                      </div>

                      <!--
                      Customer role select will be used if there is more than one customer role option.
                      <v-select
                        v-model="customer.companyRole"
                        :items="companyRoleItems"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        hide-details
                        :menu-props="{ maxHeight: '400' }"
                        label="Company Role"
                        :persistent-hint="false"
                        clearable
                        class="max-h">
                        <template #item="{ item, attrs, on }">
                          <v-list-item v-bind="attrs" v-on="on">
                            <v-tooltip right>
                              <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                <v-list-item-content v-bind="tooltipAttrs" v-on="tooltipOn">
                                  <v-list-item-title>
                                    {{ item.text || item }}
                                  </v-list-item-title>
                                </v-list-item-content>
                              </template>

                              <span>
                                Allows adding, updating, and removing this company's authorized employees using the selected account.
                              </span>
                            </v-tooltip>
                          </v-list-item>
                        </template>
                      </v-select> -->
                    </v-col>

                    <!-- <v-col cols="12" lg="4" md="6" sm="6" xs="12">
                  <v-autocomplete clearable v-if="accountForCustomers.length != 0"
                    class="account-connection-autocomplete" style="max-height:40px;min-height:40px" outlined
                    v-model="customer.account.id" :items="accountForCustomers" item-value="id" item-text="fullName"
                    dense hide-details :menu-props="{ maxHeight: '200px' }" placeholder="Account For Customer"
                    label="Account For Customer" :persistent-hint="false">

                    <template v-slot:selection="{ props, item }">
                      <div class="d-flex flex-column gap-4">
                        <div>
                          {{ item.fullName }}
                        </div>
                      </div>
                    </template>

                    <template v-slot:item="{ props, item }">
                      <div class="d-flex flex-column gap-2">
                        <div>
                          <strong>{{ item.fullName }}</strong>
                        </div>
                        <div style="font-size: 14px">
                          {{ item.email }}
                        </div>
                      </div>
                    </template>
                  </v-autocomplete>

                  <v-select
                    v-if="accountForCustomers.length === 0"
                    :disabled="accountForCustomers.length === 0"
                    style="height: 40px"
                    outlined
                    v-model="customer.account.id"
                    :items="accountForCustomers"
                    item-value="id"
                    dense
                    hide-details
                    :menu-props="{ maxHeight: '400' }"
                    label="Plase add customer for connect account"
                    :persistent-hint="false">
                  </v-select>
                </v-col> -->

                    <v-col cols="12" lg="4" md="4" sm="6" xs="12">
                      <v-btn
                        color="accent"
                        style="height: 40px; white-space: nowrap"
                        dark
                        height="100%"
                        block
                        small
                        @click.prevent="addCustomer">
                        Add Employee
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>

                <v-dialog
                  v-model="createAccountDialog"
                  :loading="isLoading"
                  max-width="60%"
                  @click:outside="closeAccountDialog">
                  <v-card>
                    <v-toolbar fixed color="titlebg" class="title" dense dark>
                      <v-toolbar-title>Create Account</v-toolbar-title>
                      <v-spacer></v-spacer>
                      <v-btn icon @click="closeAccountDialog">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-toolbar>
                    <AccountRegister
                      :key="accountDialogKey"
                      :isPopUp="true"
                      :preparedData="customer"
                      v-on:registerDone="onAccountRegistered" />
                  </v-card>
                </v-dialog>
              </div>

              <v-spacer style="margin-bottom: 16px"></v-spacer>

              <div class="text-center">
                <v-progress-circular
                  :size="50"
                  color="primary"
                  class="mt-2"
                  v-if="isLoading"
                  indeterminate></v-progress-circular>
              </div>
              <div
                v-for="(item, index) in companyData.customers"
                v-if="!saveLoader"
                :key="index"
                class="my-0 pt-4 d-flex flex-column customer-card">
                <v-row>
                  <v-col>
                    <v-row>
                      <v-col cols="12" lg="3" md="4" sm="6" xs="12" style="position: relative">
                        <v-icon
                          color="mango-red"
                          style="position: absolute; z-index: 999999; right: 25px; top: 23px"
                          @click="copyToClipboard(item.firstName)"
                          small>
                          mdi-content-copy
                        </v-icon>
                        <v-text-field outlined label="First Name" hide-details dense v-model="item.firstName" />
                      </v-col>

                      <v-col cols="12" lg="3" md="4" sm="6" xs="12" style="position: relative">
                        <v-icon
                          color="mango-red"
                          style="position: absolute; z-index: 999999; right: 25px; top: 23px"
                          @click="copyToClipboard(item.lastName)"
                          small>
                          mdi-content-copy
                        </v-icon>
                        <v-text-field outlined label="Last Name" hide-details dense v-model="item.lastName" />
                      </v-col>

                      <v-col cols="12" lg="3" md="4" sm="6" xs="12" style="position: relative">
                        <v-icon
                          color="mango-red"
                          style="position: absolute; z-index: 999999; right: 25px; top: 23px"
                          @click="copyToClipboard(item.email)"
                          small>
                          mdi-content-copy
                        </v-icon>
                        <v-text-field outlined label="E-mail" hide-details dense v-model="item.email" />
                      </v-col>

                      <v-col cols="12" lg="3" md="4" sm="6" xs="12" style="position: relative">
                        <v-icon
                          color="mango-red"
                          style="position: absolute; z-index: 999999; right: 25px; top: 23px"
                          @click="copyToClipboard(item.phone)"
                          small>
                          mdi-content-copy
                        </v-icon>
                        <CompanyPhoneInput
                          v-model="item.phone"
                          label="Phone"
                          :required="!item.companyCustomerNumber" />
                      </v-col>

                      <v-col cols="12" lg="3" md="4" sm="6" xs="12" style="position: relative">
                        <v-icon
                          color="mango-red"
                          style="position: absolute; z-index: 999999; right: 25px; top: 23px"
                          @click="copyToClipboard(item.companyCustomerNumber)"
                          small>
                          mdi-content-copy
                        </v-icon>
                        <v-text-field
                          outlined
                          label="IATA Number"
                          hide-details
                          dense
                          v-model="item.companyCustomerNumber" />
                      </v-col>

                      <v-col cols="12" lg="3" md="4" sm="6" xs="12" style="position: relative">
                        <v-icon
                          color="mango-red"
                          style="position: absolute; z-index: 999999; right: 25px; top: 23px"
                          @click="copyToClipboard(item.reference)"
                          small>
                          mdi-content-copy
                        </v-icon>
                        <v-text-field outlined label="Reference" hide-details dense v-model="item.reference" />
                      </v-col>

                      <v-col cols="12" lg="3" md="4" sm="6" xs="12">
                        <AccountAutoComplete
                          :initItem="item"
                          :isEmployee="true"
                          :accountForCustomers="accountForCustomers"
                          @modelChange="accountChangeItem($event, item)"
                          dense />
                      </v-col>

                      <v-col cols="12" lg="3" md="4" sm="6" xs="12" class="d-flex align-center">
                        <div class="d-flex align-center">
                          <v-switch
                            v-model="item.companyRole"
                            true-value="employee_manager"
                            :false-value="null"
                            color="greeny"
                            hide-details
                            label="Can manage authorized employees"
                            class="pa-0 ma-0" />
                          <v-tooltip bottom max-width="280">
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-bind="attrs" v-on="on" color="grey" size="24" class="ml-2"
                                >mdi-information-outline</v-icon
                              >
                            </template>
                            <span
                              >Allows adding, updating, and removing this company's authorized employees using the
                              selected account.</span
                            >
                          </v-tooltip>
                        </div>

                        <!--
                      Customer role select will be used if there is more than one customer role option.
                          outlined
                          v-model="item.companyRole"
                          :items="companyRoleItems"
                          item-text="text"
                          item-value="value"
                          dense
                          hide-details
                          :menu-props="{ maxHeight: '400' }"
                          label="Company Role"
                          :persistent-hint="false"
                          clearable
                          class="max-h">
                          <template #item="{ item, attrs, on }">
                            <v-list-item v-bind="attrs" v-on="on">
                              <v-tooltip right>
                                <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                  <v-list-item-content v-bind="tooltipAttrs" v-on="tooltipOn">
                                    <v-list-item-title>
                                      {{ item.text || item }}
                                    </v-list-item-title>
                                  </v-list-item-content>
                                </template>

                                <span>
                                  Allows adding, updating, and removing this company's authorized employees using the selected account.
                                </span>
                              </v-tooltip>
                            </v-list-item>
                          </template>
                        </v-select> -->
                      </v-col>

                      <!-- <v-col cols="12" lg="3" md="6" sm="6" xs="12">

                    <v-autocomplete clean label="Account For Customer" clearable v-if="accountForCustomers.length != 0"
                      class="account-connection-autocomplete" style="max-height:40px;min-height:40px" outlined
                      v-model="item.account.id" :items="accountForCustomers" item-text="fullName" item-value="id" dense
                      hide-details :menu-props="{ maxHeight: '200px' }" :persistent-hint="false">

          

                      <template v-slot:item="{ props, item }">
                        <div class="d-flex flex-column gap-2">
                          <div>
                            <strong>{{ item.fullName }}</strong>
                          </div>
                          <div style="font-size: 14px">
                            {{ item.email }}
                          </div>
                        </div>
                      </template>
                    </v-autocomplete>

                    <v-select v-if="accountForCustomers.length == 0" :disabled="accountForCustomers.length == 0"
                      style="height:40px" outlined :items="accountForCustomers" item-text="fullName" item-value="id"
                      dense hide-details :menu-props="{ maxHeight: '400' }"
                      label="Plase add customer for connect account" :persistent-hint="false"></v-select>
                  </v-col> -->
                    </v-row>
                  </v-col>

                  <v-col cols="12" lg="1" md="1" sm="4" xs="12">
                    <v-btn color="primary" style="min-height: 40px" block small dark @click="removeCustomer(index)">
                      REMOVE
                    </v-btn>
                  </v-col>
                </v-row>
                <v-divider v-if="index < companyData.customers.length - 1" class="my-2" />
              </div>
            </div>

            <alert
              v-if="warnNoEmployee && companyData.customers && companyData.customers.length === 0"
              type="error"
              color="primary"
              textBold="You must add at least one authorized employee for the company you will add." />
          </v-card-text>

          <v-card-actions class="px-7 py-4 dialog-actions">
            <v-spacer></v-spacer>
            <v-btn color="primary" :disabled="isLoading || saveLoader" @click="closeDialog">Cancel</v-btn>

            <v-btn
              :loading="saveLoader"
              :disabled="isLoading || saveLoader"
              color="accent"
              @click="createOrUpdateCompany">
              {{ editing ? "Update" : "Create" }} Company
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- COMPANY DETAIL DIALOG -->
    <v-dialog
      v-model="detailDialog"
      :loading="isLoading"
      width="1200px"
      class="pa-5"
      @click:outside="detailDialog = false">
      <v-card>
        <v-toolbar fixed color="titlebg" class="title" dense dark>
          <v-toolbar-title class="ml-2">Company: {{ companyData && companyData.name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <companyDetail v-if="companyData && detailDialog" :company="companyData" :loading="isLoading" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import alert from "@/components/common/alert";
import copyToClipboardField from "@/components/common/copyToClipboardField";
import companyDetail from "@/components/info/companyDetail";
import common from "@/mixins/common";
import router from "@/router";
import AuthApi from "@/services/auth.api";
import store from "@/store";
import { copyToClipboard, debounce, getErrorMessage, validator } from "@/util/helpers";
import Trainplanet from "@/util/trainplanet.api";
import AccountRegister from "@/views/User/Account/account-register.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mapGetters } from "vuex";
import countryList from "../../../static/countries.json";
import carrierList from "@/assets/carriers.json";
import AccountAutoComplete from "./AccountAutoComplete.vue";
import CompanyPhoneInput from "./CompanyPhoneInput.vue";
import { ROLES } from "@/store/modules/roles";

export default {
  components: {
    alert,
    companyDetail,
    ValidationObserver,
    ValidationProvider,
    copyToClipboardField,
    AccountRegister,
    AccountAutoComplete,
    CompanyPhoneInput,
  },
  mixins: [common],
  data: () => ({
    newCampaignCode: { title: "", code: "", hidden: false },
    newAgreementCode: "",
    newAgreementCarrier: null,
    agreementCarriers: carrierList,
    accountLimit: 20,
    options: {},
    companiesTotalCount: 0,
    warnNoEmployee: false,
    countries: countryList,
    validate: {
      greater: validator.greaterZeroCheck(),
      input: validator.required(),
      postalcode: validator.postalCode(),
      email: validator.email(),
      orderNumber: validator.orderNumber(),
      select: validator.select(),
    },
    detailDialog: false,
    detail: null,
    customer: {
      firstName: null,
      lastName: null,
      phone: null,
      email: null,
      companyCustomerNumber: null,
      companyRole: null,
      account: {
        id: "",
      },
    },
    dialog: false,
    createAccountDialog: false,
    editing: true,
    saveLoader: false,
    isLoading: false,
    searchQuery: "",
    accountForCustomers: "",
    isCustomerAddingCollapseOpen: false,
    dtoCompanyData: {
      iataNumber: null,
      reference: null, //other
      fortnoxCustomerId: null,
      contactPersonName: null,
      invoiceEmail: null,
      name: null,
      organizationNumber: null,
      phone: null,
      email: null,
      tenants: [],
      limit: {
        amount: 0,
        currency: "SEK",
      },
      comment: null,
      customers: [],
      trusted: true,
      automaticInvoiceFeeEnabled: true,
      allowedDaysToPay: 10,
      invoicingFrequency: "never",
      invoicingMethod: "in_bulk",
      vatNumber: null,
      address: null,
      postalCode: null,
      city: null,
      country: null,
      defaultCampaignCodes: [],
      defaultAgreementCodes: [],
      category: "company",
    },
    companyData: {
      iataNumber: null,
      reference: null, //other
      fortnoxCustomerId: null,
      contactPersonName: null,
      invoiceEmail: null,
      name: null,
      organizationNumber: null,
      phone: null,
      email: null,
      tenants: [],
      limit: {
        amount: 0,
        currency: "SEK",
      },
      comment: null,
      customers: [],
      trusted: true,
      automaticInvoiceFeeEnabled: true,
      allowedDaysToPay: 10,
      invoicingFrequency: "never",
      invoicingMethod: "in_bulk",
      vatNumber: null,
      address: null,
      postalCode: null,
      city: null,
      country: null,
      defaultAgreementCodes: [],
      defaultCampaignCodes: [],
      category: "company",
    },
    loading: false,
    companiesRequestId: 0,
    selectedTenants: [],
    selectedCategory: null,
    companies: [],
    headers: [
      {
        text: "Company Name",
        value: "name",
      },
      {
        text: "Organization Number",
        value: "organizationNumber",
        width: 180,
      },
      {
        text: "Fortnox Customer ID",
        value: "fortnoxCustomerId",
        width: 200,
      },
      {
        text: "Phone Number",
        value: "phone",
      },
      {
        text: "E-Mail",
        value: "email",
        width: 220,
      },
      {
        text: "Limit",
        value: "limit.plainText",
        align: "end",
      },
      {
        text: "Remaining Limit",
        value: "remainingLimit.plainText",
        align: "end",
      },
      {
        text: "Actions",
        value: "actions",
        align: "end",
        width: 150,
      },
    ],
    invoicingFrequencyItems: [
      { text: "Never", value: "never" },
      { text: "Minutely", value: "minutely" },
      { text: "Hourly", value: "hourly" },
      { text: "Daily", value: "daily" },
      { text: "Weekly", value: "weekly" },
      { text: "Monthly", value: "monthly" },
    ],
    invoicingMethodItems: [
      { text: "Bulk invoicing", value: "in_bulk" },
      { text: "Per-order invoicing", value: "individually" },
    ],
    companyRoleItems: [{ text: "Employee Manager", value: "employee_manager" }],
    skipInitialOptionsWatcher: true,
    accountDialogKey: 0, // incremented on each open to force AccountRegister re-mount
    companyCategories: [
      { text: "Company", value: "company" },
      { text: "Association", value: "association" },
    ],
  }),
  created() {
    this.getToken();
    this.getTenant();
  },
  computed: {
    AuthModel() {
      return new AuthApi(this.token);
    },
    ...mapGetters({
      tenants: "auth/userTenants",
      isBackOfficeAdmin: "auth/isBackOfficeAdmin",
      isSuperAdmin: "auth/isSuperAdmin",
      userRoles: "auth/userRoles",
    }),
    visibleChipLength() {
      const screenWidth = this.$vuetify.breakpoint.width;
      switch (true) {
        case screenWidth >= 1600:
          return 2;
        case screenWidth >= 1400:
          return 3;
        case screenWidth >= 960:
          return 1;
        case screenWidth >= 768:
          return 6;
        case screenWidth >= 500:
          return 2;
        default:
          return 1;
      }
    },
    visibleTenantFilterChipLength() {
      const screenWidth = this.$vuetify.breakpoint.width;
      switch (true) {
        case screenWidth >= 1920:
          return 2;
        case screenWidth >= 1600:
          return 2;
        case screenWidth >= 1264:
          return 1;
        case screenWidth >= 960:
          return 2;
        default:
          return 2;
      }
    },
    canManageCompanies() {
      return this.isBackOfficeAdmin || this.userRoles.includes(ROLES.COMPANY_MANAGE);
    },
    canCreateAccount() {
      return this.isBackOfficeAdmin || this.userRoles.includes(ROLES.ACCOUNT_MANAGER);
    },
  },
  watch: {
    options: {
      deep: true,
      handler: debounce(function () {
        // skip the initial options watcher run to prevent getCompanies request, which is triggered when Vuetify sets the table options
        // initial getCompanies request is handled by the selectedTenants watcher
        if (this.skipInitialOptionsWatcher) {
          this.skipInitialOptionsWatcher = false;
          return;
        }
        this.getCompanies();
      }, 500),
    },
    detailDialog: {
      deep: true,
      handler: function () {
        if (this.detail) {
          this.initCustomersByCompanyId(this.detail.id);
        }
      },
    },
    searchQuery: {
      deep: true,
      handler: debounce(function () {
        this.getCompanies();
      }, 200),
    },
    selectedCategory: {
      handler: function () {
        this.getCompanies();
      },
    },
    tenants: {
      handler: function () {
        const tenantIds = this.tenants.map((item) => (typeof item === "string" ? item : item.id));

        const hasSameTenantIds =
          this.selectedTenants.length === tenantIds.length &&
          this.selectedTenants.every((id, index) => id === tenantIds[index]);

        if (hasSameTenantIds) return;

        this.selectedTenants = tenantIds;
      },
    },
    // initial company fetch is triggered here after selectedTenants is populated on tenants watcher
    selectedTenants: {
      deep: true,
      handler: debounce(function () {
        if (this.tenants.length > 0) {
          this.getCompanies();
        }
      }, 500),
    },
  },
  methods: {
    normalizeCompanyData(item) {
      return {
        ...item,
        defaultCampaignCodes: (item.defaultCampaignCodes || []).map((c) =>
          typeof c === "string" ? { code: c, title: null, hidden: false, applyByDefault: false } : c
        ),
        category: item.category || "company",
      };
    },
    addCampaignCode() {
      const code = (this.newCampaignCode.code || "").trim();
      const title = (this.newCampaignCode.title || "").trim();
      const hidden = this.newCampaignCode.hidden;
      const applyByDefault = this.newCampaignCode.applyByDefault;

      if (!code) {
        this.$store.dispatch("error", "Please enter a campaign code.");
        return;
      }

      if (hidden && !title) {
        this.$store.dispatch("error", "Title is required when the campaign code is hidden.");
        return;
      }

      if (!this.companyData.defaultCampaignCodes) {
        this.companyData.defaultCampaignCodes = [];
      }

      const isDuplicate = this.companyData.defaultCampaignCodes.some((c) => c.code === code);
      if (isDuplicate) {
        this.$store.dispatch("error", `Campaign code "${code}" already exists.`);
        return;
      }

      this.companyData.defaultCampaignCodes.push({ code, title: title || null, hidden, applyByDefault });

      this.newCampaignCode = { title: "", code: "", hidden: false, applyByDefault: false };
      this.$store.dispatch("error", null);
    },

    removeCampaignCode(index) {
      this.companyData.defaultCampaignCodes.splice(index, 1);
    },
    addAgreementCode() {
      if (!this.newAgreementCode || !this.newAgreementCode.trim()) {
        this.$store.dispatch("error", "Please enter an agreement code.");
        return;
      }

      if (!this.newAgreementCarrier) {
        this.$store.dispatch("error", "Please select a carrier.");
        return;
      }

      this.companyData.defaultAgreementCodes.push({
        code: this.newAgreementCode.trim(),
        issuer: `urn:x_swe:carrier:${parseInt(this.newAgreementCarrier.value, 10)}`,
      });

      this.newAgreementCode = "";
      this.newAgreementCarrier = null;
      this.$store.dispatch("error", null);
    },
    removeAgreementCode(index) {
      this.companyData.defaultAgreementCodes.splice(index, 1);
    },
    getCarrierText(value) {
      const numericValue = parseInt(value.split(":").pop(), 10);

      const carrier = this.agreementCarriers.find((c) => c.value === numericValue);
      return carrier ? carrier.text : value;
    },
    accountChange(value) {
      this.customer.account.id = value?.id;
      this.customer.account.fullName = value?.fullName;
      this.customer.account.email = value?.email;
    },
    accountChangeItem(value, item) {
      if (!value) {
        this.$set(item, "account", null);
        return;
      }

      this.$set(item, "account", {
        id: value.id,
      });
    },
    async getAccountList() {
      const params = {
        includeDeleted: false,
      };

      try {
        const response = await this.AuthModel.accountList(params);
        this.accountForCustomers = response.data.filter((account) => !account.isDeleted);
      } catch (error) {
        console.error(error);
      }
    },
    async getTenant() {
      const res = await this.$store.dispatch("auth/getUser");
      this.allTenants = res.tenants;
    },
    async getToken() {
      this.token = await store.dispatch("auth/acquireToken", router.history.current.meta.scopes);
      this.getAccountList();
    },
    copyToClipboard,
    clearCompanyData() {
      this.companyData = { ...this.dtoCompanyData };
      this.companyData.customers = [];
      this.customer = {
        firstName: null,
        lastName: null,
        phone: null,
        email: null,
        companyCustomerNumber: null,
        companyRole: null,
        account: {
          id: "",
        },
      };
    },
    closeDialog() {
      if (this.isLoading) return;
      this.editing = false;
      this.dialog = false;
      this.createAccountDialog = false;
    },
    closeAccountDialog() {
      this.createAccountDialog = false;
    },
    async onAccountRegistered(createdAccount) {
      this.closeAccountDialog();
      await this.getAccountList();

      if (!createdAccount?.id) return;

      const accounts = Array.isArray(this.accountForCustomers) ? this.accountForCustomers : [];
      const account = accounts.find((item) => item.id === createdAccount.id) ?? createdAccount;

      this.$set(this.customer, "account", {
        id: account.id,
        fullName: account.fullName,
        email: account.email,
      });
    },
    async openCreateCompanyForm() {
      this.dialog = true;
      this.editing = false;
      this.warnNoEmployee = false;
      this.clearCompanyData();
    },
    validateEmail(email) {
      // Regular expression for validating email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    async addCustomer() {
      const mailControl = this.validateEmail(this.customer.email);
      if (this.customer.companyCustomerNumber) {
        // The form is not validated for an IATA-only employee, but a phone given with it must be valid.
        if (!this.$refs.employeePhone.validate(true)) return;

        this.companyData.customers.push({
          ...this.customer,
          delivery: { ...this.customer },
          billing: { ...this.customer },
          company: { ...this.customer },
          payment: { ...this.customer },
        });

        this.customer = {
          firstName: null,
          lastName: null,
          phone: null,
          email: null,
          companyCustomerNumber: "",
          companyRole: null,
          account: {
            id: null,
            isDeleted: false,
          },
        };
      } else {
        if (mailControl) {
          if (this.$refs[`employeeForm`].validate()) {
            this.companyData.customers.push({
              ...this.customer,
              delivery: { ...this.customer },
              billing: { ...this.customer },
              company: { ...this.customer },
              payment: { ...this.customer },
            });

            this.customer = {
              firstName: null,
              lastName: null,
              phone: null,
              email: null,
              companyCustomerNumber: "",
              companyRole: null,
              account: {
                id: null,
                isDeleted: false,
              },
            };
          }
        } else {
          await this.$store.dispatch("error", "E-Mail format is not valid");
        }
      }
    },
    removeCustomer(index) {
      this.isLoading = true;
      this.companyData.customers.splice(index, 1);
      this.isLoading = false;
    },

    async getCompanies() {
      const requestId = ++this.companiesRequestId;
      this.loading = true;
      try {
        const params = {
          tenantIds: this.selectedTenants,
          page: this.options.page,
          q: this.searchQuery,
          limit: this.options.itemsPerPage,
          offset: this.options.itemsPerPage * (this.options.page - 1),
        };

        if (this.selectedCategory) {
          params.category = this.selectedCategory;
        }

        const response = await Trainplanet.getCompanies(params);
        // ignore stale responses from requests superseded by a newer one
        if (requestId !== this.companiesRequestId) return;
        this.companies = response.company;
        this.companiesTotalCount = parseInt(response.meta.total, 10);
      } catch (error) {
        if (requestId !== this.companiesRequestId) return;
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        if (requestId === this.companiesRequestId) {
          this.loading = false;
        }
      }
    },
    async createOrUpdateCompany() {
      if (this.$refs[`companyForm`].validate()) {
        this.saveLoader = true;

        this.companyData.customers.forEach((element) => {
          if (element.account && !element.account.id) {
            const propToBeDeleted = "account";
            delete element[propToBeDeleted];
          } else {
            if (element.account && element.account.isDeleted == null) {
              element.account.isDeleted = false;
            }
          }
        });

        if (
          !this.companyData.tenants.length &&
          !this.companyData.tenants.includes("6ef7e19b-69b0-4805-b8a0-81bf7c3ca1d9")
        ) {
          this.companyData.tenants.push("6ef7e19b-69b0-4805-b8a0-81bf7c3ca1d9");
        }

        if (this.companyData.customers.length === 0) {
          this.warnNoEmployee = true;
          this.saveLoader = false;
        } else {
          try {
            if (this.editing) {
              await Trainplanet.updateCompany(this.companyData.id, this.companyData);
            } else {
              await Trainplanet.createCompany(this.companyData);
            }
            await this.$store.dispatch(
              "success",
              `You have successfully ${this.editing ? "updated" : "created"} a company!`
            );
            await this.getCompanies();
            this.dialog = false;
            this.clearCompanyData();
          } catch (error) {
            const id = this.companyData.id;
            await this.initCustomersByCompanyId(id);
            await this.$store.dispatch("error", getErrorMessage(error));
            this.saveLoader = false;
          } finally {
            this.saveLoader = false;
          }
        }
      }
    },
    async deleteCompany(companyId) {
      this.$confirm("Are you sure that you want to delete this company?", {
        color: "primary",
        title: "Delete Company",
      }).then(async (res) => {
        if (!res) return;
        await this.$store.dispatch("loading", true);
        try {
          await Trainplanet.deleteCompany(companyId);
          await this.$store.dispatch("success", "You have successfully deleted a company!");
          await this.getCompanies();
        } catch (error) {
          await this.$store.dispatch("error", getErrorMessage(error));
        } finally {
          await this.$store.dispatch("loading", false);
        }
      });
    },
    async initCustomersByCompanyId(companyId) {
      this.customer = {
        firstName: null,
        lastName: null,
        phone: null,
        email: null,
        companyCustomerNumber: null,
        companyRole: null,
        account: {
          id: "",
        },
      };

      try {
        this.isLoading = true;
        const res = await Trainplanet.getCustomerList({ companyId });
        res.customers.forEach((element) => {
          if (element.accountInfo && element.accountInfo.id) {
            element.account = {
              id: element.accountInfo.id,
              fullName: element.accountInfo.fullName,
              isDeleted: element.accountInfo.isDeleted,
              email: element.accountInfo.email,
            };

            const propToBeDeleted = "accountId";
            delete element[propToBeDeleted];
            const propToBeDeleted2 = "accountInfo";
            delete element[propToBeDeleted2];
          }
        });
        this.companyData.customers = res.customers;
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-input__slot {
  padding: 0 30px 0 9px;
}

.form-section {
  background: #ffffff;
  border-radius: 12px;
  padding: 4px 12px 8px;
  margin-bottom: 10px;
}

.add-box {
  background: #f8fafc;
  border: 1px solid #e1e8f0;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
}

.customer-card {
  gap: 10px;
}

.scroll-box {
  max-height: 200px;
  overflow-y: auto;
}

.chip-key {
  color: #888;
  font-size: 12px;
  font-weight: bold;
  margin-right: 4px;
  letter-spacing: 0.5px;
}

.field-status-chip {
  font-size: 9px !important;
  font-weight: 700 !important;
  height: 18px !important;
  padding: 0 6px !important;
  letter-spacing: 0.5px !important;
}

.chip-bool-yes {
  color: #fff !important;
}

.chip-bool-no {
  color: #555 !important;
}

.chip-val {
  font-weight: 700;
  color: #424242;
  margin-left: 2px;
}

.dialog-actions {
  position: relative;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 -2px 6px rgba(16, 24, 40, 0.2);
}
</style>
